import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { subDays, startOfDay } from 'date-fns';

interface WidgetData {
  // Tracking stats
  cigarettesToday: number;
  cigarettesTotal: number;
  stressLevel: number;
  moodRating: number;
  cravingsCount: number;
  anxietyLevel: number;
  
  // Streak data
  currentStreak: number;
  longestStreak: number;
  
  // Goal progress
  dailyGoal: number;
  dailyProgress: number;
  weeklyGoal: number;
  weeklyProgress: number;
  
  // Chart data
  weeklyTrend: Array<{ date: string; value: number }>;
  moodTrend: Array<{ date: string; value: number }>;
  
  // Engagement
  totalSessions: number;
  activitiesCompleted: number;
  articlesRead: number;
  
  // Raw arrays for widgets that need them
  progressLogs: Array<{
    log_date: string;
    cigarettes_smoked: number | null;
    mood_rating: number | null;
    stress_level: number | null;
    cravings_count: number | null;
    anxiety_level: number | null;
  }>;
}

export function useDashboardWidgetData(userId: string | undefined, productId: string) {
  return useQuery({
    queryKey: ['dashboard-widget-data', userId, productId],
    queryFn: async (): Promise<WidgetData> => {
      if (!userId) throw new Error('No user ID');
      
      const today = startOfDay(new Date());
      const weekAgo = subDays(today, 7);
      
      // Fetch progress logs for the past 30 days
      const { data: progressLogs, error: logsError } = await supabase
        .from('progress_logs')
        .select('*')
        .eq('user_id', userId)
        .eq('product_id', productId)
        .gte('log_date', subDays(today, 30).toISOString())
        .order('log_date', { ascending: false });
      
      if (logsError) {
        console.error('Error fetching progress logs:', logsError);
      }
      
      const logs = progressLogs || [];
      
      // Calculate today's stats
      const todayLog = logs.find(l => 
        startOfDay(new Date(l.log_date)).getTime() === today.getTime()
      );
      
      // Calculate weekly trend
      const weeklyTrend: Array<{ date: string; value: number }> = [];
      const moodTrend: Array<{ date: string; value: number }> = [];
      for (let i = 6; i >= 0; i--) {
        const date = subDays(today, i);
        const dateStr = date.toISOString().split('T')[0];
        const dayLog = logs.find(l => l.log_date.startsWith(dateStr));
        weeklyTrend.push({
          date: dateStr,
          value: dayLog?.cigarettes_smoked ?? 0,
        });
        moodTrend.push({
          date: dateStr,
          value: dayLog?.mood_rating ?? 0,
        });
      }
      
      // Calculate streak (consecutive days with logs)
      let currentStreak = 0;
      let checkDate = today;
      while (true) {
        const dateStr = checkDate.toISOString().split('T')[0];
        const hasLog = logs.some(l => l.log_date.startsWith(dateStr));
        if (hasLog) {
          currentStreak++;
          checkDate = subDays(checkDate, 1);
        } else {
          break;
        }
        if (currentStreak > 365) break; // Safety limit
      }
      
      // Fetch engagement summary
      const { data: engagement } = await supabase
        .from('user_engagement_summary')
        .select('*')
        .eq('user_id', userId)
        .eq('product_id', productId)
        .single();
      
      // Calculate totals
      const cigarettesTotal = logs.reduce((sum, l) => sum + (l.cigarettes_smoked || 0), 0);
      const weekLogs = logs.filter(l => new Date(l.log_date) >= weekAgo);
      const weeklyProgress = weekLogs.reduce((sum, l) => sum + (l.cigarettes_smoked || 0), 0);
      
      return {
        cigarettesToday: todayLog?.cigarettes_smoked ?? 0,
        cigarettesTotal,
        stressLevel: todayLog?.stress_level ?? 0,
        moodRating: todayLog?.mood_rating ?? 0,
        cravingsCount: todayLog?.cravings_count ?? 0,
        anxietyLevel: todayLog?.anxiety_level ?? 0,
        
        currentStreak,
        longestStreak: engagement?.longest_streak_days ?? currentStreak,
        
        dailyGoal: 10, // Could come from user preferences
        dailyProgress: todayLog?.cigarettes_smoked ?? 0,
        weeklyGoal: 50,
        weeklyProgress,
        
        weeklyTrend,
        moodTrend,
        
        totalSessions: engagement?.total_sessions ?? 0,
        activitiesCompleted: engagement?.activities_completed ?? 0,
        articlesRead: engagement?.articles_read ?? 0,
        
        progressLogs: logs,
      };
    },
    enabled: !!userId,
    staleTime: 60000, // 1 minute
  });
}
